// Hvilken slags ledning bliver det? Søkabel ud til havet, ellers luftledning, ellers jordkabel.
// Holder spilleren Shift nede, vælges jordkabel før luftledning, når banen har kabler.

import type { ActionKind, ApplyError, LineMedium, Mechanic, State } from '../sim';
import { check } from '../sim/actions';
import { allowedMedia, nodeById, pairKey } from '../sim/grid';

export type BuildCheck = { ok: true; medium: LineMedium; cost: number; target: string } | { ok: false; error: ApplyError };

export function chooseBuild(state: State, mechanics: readonly Mechanic[], aId: string, bId: string, preferCable = false): BuildCheck {
  const a = nodeById(state, aId);
  const b = nodeById(state, bId);
  if (!a || !b || a.id === b.id) return { ok: false, error: 'unknown_target' };
  const media = allowedMedia(state, a, b);
  const target = pairKey(aId, bId);
  const order: LineMedium[] = preferCable ? ['sea', 'cable', 'air'] : ['sea', 'air', 'cable'];
  let firstError: ApplyError | null = null;
  for (const m of order) {
    if (!media.includes(m)) continue;
    if (m !== 'sea' && !mechanics.includes(m as Mechanic)) continue;
    const c = check(state, 'build_line', target, m);
    if (c.ok) return { ok: true, medium: m, cost: c.cost, target };
    firstError ??= c.error;
  }
  if (firstError) return { ok: false, error: firstError };
  // Ingen tilladte medier: find den rigtige grund via luftledningens tjek.
  const c = check(state, 'build_line', target, media[0] ?? 'air');
  return c.ok ? { ok: false, error: 'not_allowed' } : { ok: false, error: c.error };
}

/** Kort grund til ringmenuen og prisboblen. */
export function shortReason(e: ApplyError | 'paused', kind?: ActionKind): string {
  if (kind === 'upgrade_line' && e === 'not_allowed') return 'Fuldt udbygget';
  if (kind === 'demolish' && e === 'not_allowed') return 'Hører til banen';
  if (kind === 'build_line' && e === 'bad_option') return 'Kun kysten';
  if (kind === 'reinforce_mast' && e === 'not_needed') return 'Kun ved stormvarsel';
  if (kind === 'reinforce_mast' && e === 'already_exists') return 'Forstærket';
  if (kind === 'reinforce_mast' && e === 'not_allowed') return 'Kun hel luftledning';
  if (kind === 'upgrade_station' && e === 'already_exists') return 'Har station';
  if (kind === 'bury_line' && e === 'not_allowed') return 'Kan ikke graves ned';
  switch (e) {
    case 'too_poor':
      return 'Mangler penge';
    case 'not_needed':
      return 'Ikke nu';
    case 'no_crew':
      return 'Ingen ledig montør';
    case 'busy':
      return 'I gang';
    case 'not_unlocked':
      return 'Låst';
    case 'already_exists':
      return 'Findes';
    case 'too_far':
      return 'For langt';
    case 'no_slots':
      return 'Ingen pladser';
    case 'too_late':
      return 'For sent';
    case 'wrong_area':
      return 'Andet net';
    case 'paused':
      return 'Pause';
    case 'unknown_target':
      return 'Ikke her';
    case 'wrong_phase':
      return 'Ikke nu';
    case 'bad_option':
      return 'Ikke den slags';
    case 'duplicate':
      return 'Sat i gang';
    case 'other_role':
      return 'Makkers job';
    default:
      return 'Ikke muligt';
  }
}
